// Formulas written between dollar signs in text that is pasted: $x^2$ inline, $$x^2$$ on a line of its own - and
// \( ... \) and \[ ... \], which an AI's answer or a LaTeX file uses - become formulas of the editor (math-field), not
// text with the signs showing. Only where the editor takes formulas; elsewhere the text is pasted as it is.
//
// A dollar sign is also money, so a single $ opens a formula only the way TeX users write one: the character after it is
// not a space, the one before the closing $ is not a space, and the closing $ is not followed by a digit ("between $5 and
// $10" stays text, "costs $5-$10" too). A formula in $...$ stays on one line; $$...$$ and \[...\] may span lines.
// \$ is a dollar sign.

export type Piece =
  | { kind: "text"; text: string }
  | { kind: "math"; latex: string; display: boolean };

const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);
const isDigit = (c: string | undefined) => c !== undefined && c >= "0" && c <= "9";

// The closing single $ of a formula opened at `open`, or -1.
function closingDollar(text: string, open: number): number {
  for (let j = open + 1; j < text.length; j++) {
    const c = text[j];
    if (c === "\n") return -1;
    if (c === "\\") { j++; continue; }               // an escaped character, including \$
    if (c !== "$") continue;
    if (isSpace(text[j - 1]) || isDigit(text[j + 1])) continue;
    return j;
  }
  return -1;
}

export function splitMath(text: string): Piece[] {
  const pieces: Piece[] = [];
  let plain = "";
  const flush = () => { if (plain) { pieces.push({ kind: "text", text: plain }); plain = ""; } };
  let i = 0;
  // Takes the formula and moves on past it; false (and nothing taken) when there is nothing between the signs.
  const math = (latex: string, display: boolean, resumeAt: number): boolean => {
    const body = latex.replace(/\s*\n\s*/g, " ").trim();
    if (!body) return false;
    flush();
    pieces.push({ kind: "math", latex: body, display });
    i = resumeAt;
    return true;
  };

  while (i < text.length) {
    const c = text[i];
    if (c === "\\" && text[i + 1] === "$") { plain += "$"; i += 2; continue; }

    if (c === "\\" && (text[i + 1] === "(" || text[i + 1] === "[")) {
      const display = text[i + 1] === "[";
      const end = text.indexOf(display ? "\\]" : "\\)", i + 2);
      if (end > 0 && math(text.slice(i + 2, end), display, end + 2)) continue;
    }

    if (c === "$" && text[i + 1] === "$") {
      const end = text.indexOf("$$", i + 2);
      if (end > 0 && math(text.slice(i + 2, end), true, end + 2)) continue;
      plain += "$$";                 // an opening $$ with no closing one is two dollar signs, not the start of a $ formula
      i += 2;
      continue;
    } else if (c === "$" && !isSpace(text[i + 1]) && text[i + 1] !== "$") {
      const end = closingDollar(text, i);
      if (end > 0 && math(text.slice(i + 1, end), false, end + 1)) continue;
    }

    plain += c;
    i++;
  }
  flush();
  return pieces;
}

export function hasMath(text: string): boolean {
  return splitMath(text).some((p) => p.kind === "math");
}

const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escapeAttribute = (s: string) => escapeHtml(s).replace(/"/g, "&quot;");
const mathTag = (latex: string) => `<math-field data-latex="${escapeAttribute(latex)}"></math-field>`;

// Plain text with formulas, as the editor's HTML: a paragraph for each line, as an ordinary plain-text paste makes them,
// and a formula of $$ or \[ on a centred line of its own.
export function textWithMathToHtml(text: string): string {
  const paragraphs: string[] = [];
  let current = "";
  const endParagraph = () => {
    if (current.trim() || current.includes("<math-field")) paragraphs.push(`<p>${current}</p>`);
    current = "";
  };

  for (const piece of splitMath(text)) {
    if (piece.kind === "text") {
      piece.text.split(/\r\n?|\n/).forEach((line, index) => {
        if (index > 0) endParagraph();
        current += escapeHtml(line);
      });
    } else if (piece.display) {
      endParagraph();
      paragraphs.push(`<p style="text-align: center">${mathTag(piece.latex)}</p>`);
    } else {
      current += mathTag(piece.latex);
    }
  }
  endParagraph();
  return paragraphs.join("");
}

// HTML that was pasted, with its text nodes read the same way - except inside code, where a dollar sign is code. A formula
// stays where it was written, inline, whichever way it was delimited.
export function convertMathInHtml(html: string): string {
  if (!html || !/[$\\]/.test(html)) return html;
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
  const found: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (!text.parentElement?.closest("code, pre, math-field") && hasMath(text.data)) found.push(text);
  }
  if (found.length === 0) return html;

  for (const text of found) {
    const replacement = parsed.createDocumentFragment();
    for (const piece of splitMath(text.data)) {
      if (piece.kind === "text") {
        replacement.append(piece.text);
      } else {
        const field = parsed.createElement("math-field");
        field.setAttribute("data-latex", piece.latex);
        replacement.append(field);
      }
    }
    text.replaceWith(replacement);
  }
  return parsed.body.innerHTML;
}
